import { PropsWithChildren, useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Container, Title, Text, Button, Stack, Paper } from '@mantine/core';
import { IconClock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export default function ProtectedRoute({ allowedRoles }: { allowedRoles: string[] }) {
  const { user, logout } = useAuth0();
  const { t } = useTranslation();
  const role_property = import.meta.env.VITE_AUTH0_AUDIENCE + '/roles';
  const [roles, setRoles] = useState<string[] | undefined>(user && user[role_property]);

  useEffect(() => {
    const role_property = import.meta.env.VITE_AUTH0_AUDIENCE + '/roles';
    const roles = user && user[role_property];
    setRoles(roles);
  }, [user]);

  const hasAccess = allowedRoles.some((role) => roles?.includes(role));

  if (hasAccess) {
    return <Outlet />;
  } else {
    return (
      <Container size="sm" py="xl">
        <Paper withBorder shadow="md" p="xl" radius="md">
          <Stack align="center" gap="lg">
            <IconClock size={64} color="orange" />
            
            <Title order={2} ta="center">
              {t('pendingApproval.title', 'Account Pending Approval')}
            </Title>
            
            <Text ta="center" c="dimmed" size="lg">
              {t('pendingApproval.message', 'Your account has been created successfully, but access to the platform requires administrator approval due to the sensitive nature of the information contained within.')}
            </Text>
            
            <Stack gap="sm" w="100%">
              <Text fw={500} ta="center">
                {t('pendingApproval.nextSteps', 'Next Steps:')}
              </Text>
              <Text ta="center">
                {t('pendingApproval.step1', '1. An administrator has been notified of your registration')}
              </Text>
              <Text ta="center">
                {t('pendingApproval.step2', '2. You will receive an email confirmation once access is granted')}
              </Text>
              <Text ta="center">
                {t('pendingApproval.step3', '3. If urgent, please contact your system administrator directly')}
              </Text>
            </Stack>
            
            <Button 
              variant="outline" 
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            >
              {t('pendingApproval.signOut', 'Sign Out')}
            </Button>
            
            <Text size="sm" c="dimmed" ta="center">
              {t('pendingApproval.userInfo', `Logged in as: ${user?.email}`)}
            </Text>
          </Stack>
        </Paper>
      </Container>
    );
  }
}
